import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  ThemeProvider,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Restaurant,
  CheckCircle,
  LocalShipping,
  Person,
  Kitchen,
  Assignment,
  Cancel,
} from '@mui/icons-material';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { HimmaContext } from '../../../Context/HimmaContext';
import { getPresignedUrlForImage } from '../../HelperFunction/s3Service';

const TrackOrder: React.FC<{ trackingOrder: any }> = ({ trackingOrder }) => {
  const { t } = useTranslation();
  console.log('Tracking Order:', trackingOrder);
  
  if (!trackingOrder) { 
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="textSecondary">
          {t('orderManagement.orderStatus.noOrderData')}
        </Typography>
      </Box>
    );
  }

  // Extract data from the new structure
  const customerInfo = trackingOrder?.customerInfo;
  const deliveryInfo = trackingOrder?.deliveryInfo;
  const orderItems = trackingOrder?.orderItems || [];
  const invoiceDetails = trackingOrder?.invoiceDetails;
  const trackingHistory = trackingOrder?.trackingHistory || [];


  const apiClient = useContext(HimmaContext);
  const [orderDeliveryImageUrl, setOrderDeliveryImageUrl] = useState<string | null>(null);
  const [orderPickupImageUrl, setOrderPickupImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Fetch presigned URLs for images
  useEffect(() => {
    const fetchImageUrls = async () => {
      if (!apiClient || !trackingOrder) return;

      setImageLoading(true);
      try {
        const promises = [];

        if (trackingOrder.orderDeliveryImage) {
          promises.push(
            getPresignedUrlForImage(trackingOrder.orderDeliveryImage, apiClient)
              .then(url => setOrderDeliveryImageUrl(url))
              .catch(err => console.error('Error fetching delivery image URL:', err))
          );
        }

        if (trackingOrder.orderPickupImage) {
          promises.push(
            getPresignedUrlForImage(trackingOrder.orderPickupImage, apiClient)
              .then(url => setOrderPickupImageUrl(url))
              .catch(err => console.error('Error fetching pickup image URL:', err))
          );
        }

        await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching image URLs:', error);
      } finally {
        setImageLoading(false);
      }
    };

    fetchImageUrls();
  }, [trackingOrder, apiClient]);
  // Calculate subtotal from invoice details or items
  const subtotal = invoiceDetails?.subTotal || orderItems.reduce(
    (sum: number, item: any) => {
      const quantity = Number(item?.quantity ?? 0);
      // Prioritize discount prices over original price only when a valid discount exists
      const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
      const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
      const originalPrice =
        item?.originalPrice != null
          ? Number(item.originalPrice)
          : item?.unitPrice != null
            ? Number(item.unitPrice)
            : 0;
      const hasDiscount =
        discountedPrice != null &&
        discountedPrice > 0 &&
        discountedPrice < originalPrice;
      const baseUnitPrice = hasDiscount ? discountedPrice : originalPrice;
      const choicesPrice = Array.isArray(item?.choices)
        ? item.choices.reduce((choiceSum: number, choice: any) => choiceSum + (Number(choice?.choicePrice ?? 0)), 0)
        : 0;
      const mealPrice = item?.mealPrice != null ? Number(item.mealPrice) : 0;
      // const mealChoiceGroups = Array.isArray(item?.mealChoiceGroups) ? item.mealChoiceGroups : [];
      const mealChoicesUnitTotal = 0;
      const unitPrice = baseUnitPrice + choicesPrice + mealPrice + mealChoicesUnitTotal;
      return sum + (unitPrice * quantity || 0);
    },
    0
  );

  const deliveryFee = invoiceDetails?.deliveryFee || 0;
  const tax = invoiceDetails?.tax || 0;
  const serviceFee = invoiceDetails?.serviceFee || 0;
  const walletUsed = invoiceDetails?.walletUsed || 0;
  const offerDiscount = invoiceDetails?.offerDiscount || 0;

  // Grand total should include subtotal, tax, and delivery fee, minus any discounts/wallet usage.
  // Prefer the backend-calculated total when it is a positive value; otherwise compute it locally.
  const backendTotal = typeof invoiceDetails?.total === 'number' ? invoiceDetails.total : undefined;
  const grandTotal =
    backendTotal && backendTotal > 0
      ? backendTotal
      : subtotal + tax + deliveryFee - walletUsed - offerDiscount + serviceFee;

  // Check if order is cancelled
  // const isOrderCancelled = trackingOrder?.currentStatus?.toLowerCase().includes('cancelled') || 
  //                         trackingHistory.some((track: any) => track.status.toLowerCase().includes('cancelled'));

  // Function to get icon for tracking status
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'order placed':
        return <Restaurant />;
      case 'delivery partner assigned':
      case 'delivery partner accepted':
        return <Assignment />;
      case 'merchant accepted':
        return <CheckCircle />;
      case 'order preparing':
        return <Kitchen />;
      case 'order dispatched':
        return <LocalShipping />;
      case 'order delivered':
        return <Person />;
      case 'order cancelled':
        return <Cancel />;
      default:
        return <CheckCircle />;
    }
  };

  // Function to format timestamp
  // const formatTimestamp = (timestamp: string) => {
  //   const date = new Date(timestamp);
  //   return date.toLocaleString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     hour12: true
  //   });
  // };

  return (
    <ThemeProvider theme={theme}>
      <Box p={3}>
        {/* Order Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={600} mb={1}>
            {t('orderManagement.orderStatus.title')} {trackingOrder.currentStatus}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('orderManagement.orderStatus.orderId')} {trackingOrder.orderNumber}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('orderManagement.orderStatus.orderDate')} {new Date(trackingOrder.orderDate).toLocaleString()}
          </Typography>
        </Box>

        {/* Customer and Delivery Information */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                {t('orderManagement.orderStatus.customerInformation')}
              </Typography>
              <Typography variant="body2" mb={0.5}>
                <strong>{t('orderManagement.orderStatus.name')}</strong> {customerInfo?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" mb={0.5}>
                <strong>{t('orderManagement.orderStatus.email')}</strong> {customerInfo?.email || 'N/A'}
              </Typography>
              <Typography variant="body2" mb={0.5}>
                <strong>{t('orderManagement.orderStatus.phone')}</strong> {customerInfo?.phoneNumber || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                {t('orderManagement.orderStatus.deliveryInformation')}
              </Typography>
              {deliveryInfo ? (
                <>
                  <Typography variant="body2" mb={0.5}>
                    <strong>{t('orderManagement.orderStatus.partner')}</strong> {deliveryInfo.deliveryPartnerName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" mb={0.5}>
                    <strong>{t('orderManagement.orderStatus.phone')}</strong> {deliveryInfo.deliveryPartnerPhone || 'N/A'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t('orderManagement.orderStatus.noDeliveryPartner')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Product Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell>{t('orderManagement.orderStatus.image')}</TableCell>
                <TableCell>{t('orderManagement.orderStatus.product')}</TableCell>
                <TableCell>{t('orderManagement.orderStatus.store')}</TableCell>
                <TableCell align="center">{t('orderManagement.orderStatus.qty')}</TableCell>
                <TableCell align="right">{t('orderManagement.orderStatus.unitPrice')}</TableCell>
                <TableCell align="right">{t('orderManagement.orderStatus.total')}</TableCell>
                <TableCell align="center">{t('orderManagement.orderStatus.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderItems.length > 0 ? (
                orderItems.map((item: any, index: number) => (
                  <TableRow key={item.itemId || index}>
                    <TableCell>
                      <Avatar
                        src={Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl}
                        alt={item.itemName}
                        sx={{ width: 48, height: 48 }}
                        variant="square"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.itemName}
                      </Typography>
                      {item.itemVariant && item.itemVariant !== 'NA' && item.itemVariant !== 'N/A' && (
                        <Typography variant="caption" color="textSecondary">
                          {item.itemVariant}
                        </Typography>
                      )}
                      {/* Display item choices information */}
                      {item.choices && item.choices.length > 0 && (
                        <Box sx={{ mt: 0.5, p: 0.5, backgroundColor: '#fff8f0', borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            color="primary" 
                            sx={{ 
                              display: 'block',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          >
                            Choices:
                          </Typography>
                          <Box sx={{ pl: 0.25, mt: 0.15 }}>
                            {item.choices.map((choice: any, choiceIndex: number) => (
                              <Typography 
                                key={choiceIndex}
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  fontSize: '0.65rem',
                                  color: 'text.secondary'
                                }}
                              >
                                {choice.choiceName} {choice.choicePrice > 0 && `(JOD${choice.choicePrice})`}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {/* Display meal information */}
                      {item.mealId && item.mealName && (
                        <Box sx={{ mt: 0.75, p: 0.75, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            color="secondary" 
                            sx={{ 
                              display: 'block',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          >
                            🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(JOD${item.mealPrice})`}
                          </Typography>
                          {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                            <Box sx={{ pl: 0.5, mt: 0.25 }}>
                              {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                                <Typography 
                                  key={groupIndex}
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '0.65rem',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {group.choiceGroupName}: {group.choices?.map((c: any) => c.choiceName).join(', ')}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.storeName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.storeType}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        // Check if discount exists
                        const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                        const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                        const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                        const hasDiscount = discountedPrice != null && discountedPrice > 0 && discountedPrice < originalPrice;
                        const baseUnitPrice = hasDiscount ? discountedPrice : originalPrice;
                        const choicesPrice = Array.isArray(item?.choices)
                          ? item.choices.reduce((choiceSum: number, choice: any) => choiceSum + (Number(choice?.choicePrice ?? 0)), 0)
                          : 0;
                        const mealPrice = item?.mealPrice != null ? Number(item.mealPrice) : 0;
                        const unitPrice = baseUnitPrice + choicesPrice + mealPrice;
                        return (
                          <Typography variant="body2" fontWeight={hasDiscount ? 500 : 'normal'} color={hasDiscount ? 'success.main' : 'inherit'}>
                            JOD {unitPrice.toFixed(2) || '0.00'}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const quantity = Number(item?.quantity ?? 0);
                        // Check if discount exists
                        const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                        const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                        const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                        const hasDiscount = discountedPrice != null && discountedPrice > 0 && discountedPrice < originalPrice;
                        const baseUnitPrice = hasDiscount ? discountedPrice : originalPrice;
                        const choicesPrice = Array.isArray(item?.choices)
                          ? item.choices.reduce((choiceSum: number, choice: any) => choiceSum + (Number(choice?.choicePrice ?? 0)), 0)
                          : 0;
                        const mealPrice = item?.mealPrice != null ? Number(item.mealPrice) : 0;
                        const unitPrice = baseUnitPrice + choicesPrice + mealPrice;
                        const totalPrice = unitPrice * quantity;
                        
                        return (
                          <Typography variant="body2" fontWeight={600} color={hasDiscount ? 'success.main' : 'inherit'}>
                            JOD {totalPrice.toFixed(2) || '0.00'}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                    {item.merchantAccepted ? (
  <Chip 
    label={t('orderManagement.orderStatus.accepted')} 
    color="success" 
    size="small"
    sx={{ fontSize: '0.7rem' }}
  />
) : item.merchantDenied ? (
  <Chip 
    label={t('orderManagement.orderStatus.rejected')} 
    color="error" 
    size="small"
    sx={{ fontSize: '0.7rem' }}
  />
) : (
  <Chip 
    label={t('orderManagement.orderStatus.pending')} 
    color="warning" 
    size="small"
    sx={{ fontSize: '0.7rem' }}
  />
)}

                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">
                      {t('orderManagement.orderStatus.noItemsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Order Summary */}
        <Box mt={3} px={2} textAlign="right">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography>{t('orderManagement.orderStatus.subtotal')}</Typography>
                <Typography>JOD {subtotal.toFixed(2)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography>{t('orderManagement.orderStatus.tax')}</Typography>
                <Typography>JOD {tax.toFixed(2)}</Typography>
              </Box>
              {deliveryFee > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography>{t('orderManagement.orderStatus.deliveryFee')}</Typography>
                  <Typography>JOD {deliveryFee.toFixed(2)}</Typography>
                </Box>
              )}
              {serviceFee > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography>{t('orderManagement.invoice.serviceFee')}</Typography>
                  <Typography>JOD {serviceFee.toFixed(2)}</Typography>
                </Box>
              )}
              {walletUsed > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography>{t('orderManagement.orderStatus.walletUsed')}</Typography>
                  <Typography color="error">-JOD {walletUsed.toFixed(2)}</Typography>
                </Box>
              )}
              {offerDiscount > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography>{t('orderManagement.orderStatus.offerDiscount')}</Typography>
                  <Typography color="error">-JOD {offerDiscount.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight={600}>{t('orderManagement.orderStatus.grandTotal')}</Typography>
                <Typography fontWeight={600} fontSize="1.1rem">
                  JOD {grandTotal.toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography variant="caption" color="textSecondary">
                  {t('orderManagement.orderStatus.paymentMethod')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {trackingOrder.paymentMethod || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Item Details and Tracking History */}
        <Typography variant="h6" fontWeight={600} mb={2}>
          {t('orderManagement.orderStatus.orderTrackingHistory')}
        </Typography>
        {orderItems.length > 0 && trackingHistory.length > 0 && (
          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              {/* Item Details */}
              {orderItems.map((item: any, itemIndex: number) => (
                <Box key={itemIndex} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl}
                      alt={item.itemName}
                      sx={{ width: 60, height: 60, mr: 2 }}
                      variant="square"
                    />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {item.itemName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('orderManagement.orderStatus.itemPrice')} JOD {item.originalPrice?.toFixed(2) || '0.00'}
                      </Typography>
                      {/* Display meal information */}
                      {item.mealId && item.mealName && (
                        <Box sx={{ mt: 1, p: 1, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
                          <Typography 
                            variant="body2" 
                            color="secondary" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}
                          >
                            🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(JOD${item.mealPrice.toFixed(2)})`}
                          </Typography>
                          {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                            <Box sx={{ pl: 1, mt: 0.5 }}>
                              {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                                <Typography 
                                  key={groupIndex}
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {group.choiceGroupName}: {group.choices?.map((c: any) => c.choiceName).join(', ')}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Tracking History Timeline */}
                 
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                    {trackingHistory.map((track: any, index: number) => {
                      const isCancelledStatus = track.status.toLowerCase().includes('cancelled');
                      const statusColor = isCancelledStatus ? 'error.main' : 'success.main';
                      
                      return (
                        <React.Fragment key={index}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                            <Avatar
                              sx={{
                                bgcolor: statusColor,
                                width: 40,
                                height: 40,
                                color: 'white',
                                mb: 1,
                              }}
                            >
                              {getStatusIcon(track.status)}
                            </Avatar>
                            <Typography 
                              variant="caption" 
                              textAlign="center" 
                              sx={{ 
                                fontSize: '0.75rem', 
                                lineHeight: 1.2,
                                color: isCancelledStatus ? 'error.main' : 'text.primary'
                              }}
                            >
                              {track.status}
                            </Typography>
                          </Box>
                          {index < trackingHistory.length - 1 && (
                            <Box
                              sx={{
                                width: 30,
                                height: 2,
                                bgcolor: isCancelledStatus ? 'error.main' : 'success.main',
                                mx: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Order Images */}
        {(orderDeliveryImageUrl || orderPickupImageUrl) && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Order Images
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {orderPickupImageUrl && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Pickup Image
                    </Typography>
                    <Avatar
                      src={orderPickupImageUrl}
                      alt="Order Pickup"
                      sx={{ width: 120, height: 120, borderRadius: 1 }}
                      variant="square"
                    />
                  </Box>
                )}
                {orderDeliveryImageUrl && (
                  <Box>
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Delivery Image
                    </Typography>
                    <Avatar
                      src={orderDeliveryImageUrl}
                      alt="Order Delivery"
                      sx={{ width: 120, height: 120, borderRadius: 1 }}
                      variant="square"
                    />
                  </Box>
                )}
              </Box>
              {imageLoading && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Loading images...
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TrackOrder;
